import React from "react";
import type { ComponentPropsWithRef, ReactElement } from "react";
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from "react-aria-components";
import { tv, type VariantProps } from "tailwind-variants";

import { cn } from "@/lib/helpers/cn";

import { Spinner2 } from "./Spinner";

export type ButtonProps = {
  classNameChildren?: string;
  icon?: ReactElement;
  isLoading?: boolean;
  labelLoading?: string;
  rawChildren?: boolean;
  testId?: string;
} & AriaButtonProps &
  ComponentPropsWithRef<"button"> &
  VariantProps<typeof buttonStyles>;

export function Button2({
  children,
  className,
  classNameChildren,
  icon: Icon,
  isLoading = false,
  labelLoading = "Please wait...",
  rawChildren,
  size,
  variant,
  ...buttonProps
}: ButtonProps) {
  const { body, content, loadingText, overlayText, spinner } = buttonStyles({
    isLoading,
    size,
    variant,
  });

  return (
    <AriaButton
      aria-busy={isLoading}
      aria-disabled={isLoading}
      className={cn(body(), "atom-focus", className)}
      {...buttonProps}
    >
      {rawChildren && children}

      {!rawChildren && (
        <>
          <span
            aria-hidden={!isLoading}
            aria-live="polite"
            className={cn(overlayText(), loadingText())}
          >
            <Spinner2 className={spinner()} />
            {labelLoading}
          </span>

          <span aria-hidden={isLoading} className={cn(content(), classNameChildren)}>
            {Icon}
            {children}
          </span>
        </>
      )}
    </AriaButton>
  );
}

export const buttonStyles = tv({
  defaultVariants: {
    isLoading: false,
    size: "md",
    variant: "primary",
  },
  slots: {
    body: cn(
      "tw:inline-flex tw:justify-center tw:items-center tw:relative",
      "tw:outline-none",
      "tw:border tw:border-px tw:border-solid tw:rounded!",
      "tw:font-bold tw:whitespace-nowrap tw:cursor-pointer",
      "tw:transition-all tw:duration-150 ease",
    ),
    content: "tw:transition-opacity tw:duration-300 trim-both",
    loadingText: "",
    overlayText: cn(
      "tw:absolute tw:inset-0 tw:transition-opacity tw:duration-300 tw:flex tw:items-center tw:justify-center",
      "tw:pointer-events-none", // important for the Cypress test-runner to properly click the buttons
    ),
    spinner: "tw:mr-3 tw:w-5",
  },
  variants: {
    isLoading: {
      false: {
        content: "tw:opacity-100",
        loadingText: "tw:opacity-0",
      },
      true: {
        body: "tw:pointer-events-none",
        content: "tw:opacity-0",
        loadingText: "tw:opacity-100",
      },
    },
    size: {
      lg: {
        body: "tw:h-12 tw:py-3 tw:px-4 tw:!text-md",
        overlayText: "tw:leading-12",
      },
      md: {
        body: "tw:h-11 tw:py-2.5 tw:px-4 tw:!text-md",
        overlayText: "tw:leading-11",
      },
      sm: {
        body: "tw:h-9 tw:py-2 tw:px-2.5 tw:!text-sm",
        overlayText: "tw:leading-9",
        spinner: "tw:w-4 tw:mr-2",
      },
      xl: {
        body: "tw:h-14 tw:py-4 tw:px-4 tw:!text-md",
        overlayText: "tw:leading-14",
      },
      xs: {
        body: "tw:h-6 tw:py-1 tw:px-4 tw:!text-xs",
        overlayText: "",
      },
    },
    variant: {
      blank: {
        body: "tw:p-0 tw:border-transparent tw:h-auto",
      },
      "destructive-primary": {
        // temporary, awaiting Konrad's input
        body: cn(
          "tw:border-stroke-error-primary tw:bg-fill-white tw:text-text-error-primary",
          "tw:hover:border-stroke-error-secondary tw:hover:text-text-error-secondary",
          "tw:active:border-fill-error-primary tw:active:text-text-error-primary",
          "tw:disabled:border-fill-quinary tw:disabled:text-text-quaternary tw:disabled:cursor-not-allowed",
        ),
      },
      "destructive-secondary": {
        body: cn(
          "tw:bg-fill-error-primary tw:text-text-white!",
          "tw:hover:bg-fill-error-secondary",
          "tw:active:bg-fill-error-primary-w20",
          "tw:disabled:bg-fill-quaternary tw:disabled:cursor-not-allowed",
        ),
      },
      link: {
        body: cn(
          "tw:h-auto tw:p-0 tw:underline tw:hover:no-underline",
          "tw:border-transparent tw:bg-transparent tw:text-text-teal-primary",
          "tw:hover:text-text-teal-secondary",
          "tw:active:text-text-teal-primary-w20",
          "tw:disabled:border-transparent tw:disabled:text-text-quaternary tw:disabled:cursor-not-allowed",
        ),
      },
      primary: {
        body: cn(
          "tw:bg-fill-teal-primary tw:text-text-white!",
          "tw:border-fill-teal-primary",
          "tw:hover:bg-fill-teal-secondary tw:hover:text-white tw:hover:border-fill-teal-secondary",
          "tw:active:bg-fill-teal-primary-w20 tw:active:border-fill-teal-primary-w20",
          "tw:disabled:bg-fill-quaternary tw:disabled:border-fill-quaternary tw:disabled:cursor-not-allowed",
        ),
      },
      secondary: {
        body: cn(
          "tw:border-fill-teal-primary tw:bg-fill-white tw:text-text-teal-primary",
          "tw:hover:border-stroke-teal-secondary tw:hover:text-text-teal-secondary",
          "tw:active:border-fill-teal-primary-w20 tw:active:text-fill-teal-primary-w20",
          "tw:disabled:border-fill-quinary tw:disabled:text-text-quaternary tw:disabled:cursor-not-allowed",
        ),
      },
      text: {
        body: cn(
          "tw:h-auto tw:p-0",
          "tw:border-transparent tw:bg-transparent tw:text-text-teal-primary",
          "tw:hover:text-text-teal-secondary",
          "tw:active:text-text-teal-primary-w20",
          "tw:disabled:border-transparent tw:disabled:text-text-quaternary tw:disabled:cursor-not-allowed",
        ),
      },
    },
  },
});
